import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, MessageSquare, Bot, BarChart3, 
  Settings, LogOut, Menu, X, Plus, Upload, Trash2, Copy, Pencil, Code2 
} from 'lucide-react';
import { authAPI, dashboardAPI, documentAPI, conversationAPI, chatbotAPI, businessAPI } from '../services/api';

const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, statsRes] = await Promise.all([
        authAPI.getProfile(),
        dashboardAPI.getStats()
      ]);
      setUser(profileRes.data);
      console.log('Dashboard stats:', statsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const navigation = [
    { name: 'Overview', href: '/dashboard/overview', icon: LayoutDashboard },
    { name: 'Documents', href: '/dashboard/documents', icon: FileText },
    { name: 'Conversations', href: '/dashboard/conversations', icon: MessageSquare },
    { name: 'Chatbot', href: '/dashboard/chatbot', icon: Bot },
    { name: 'AI Usage', href: '/dashboard/usage', icon: BarChart3 },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <Link to="/dashboard" className="flex items-center space-x-2">
              <Bot className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-slate-900">RAGBot</span>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-500 hover:text-slate-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition ${
                    isActive 
                      ? 'bg-blue-50 text-blue-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 w-full px-4 py-3 text-slate-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-600 hover:text-slate-900"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="font-medium text-slate-900">{user?.first_name} {user?.last_name}</div>
                <div className="text-sm text-slate-500">{user?.email}</div>
              </div>
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-700 font-semibold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="p-6">
          <Routes>
            <Route path="/" element={<Overview stats={stats} refreshStats={fetchData} />} />
            <Route path="overview" element={<Overview stats={stats} refreshStats={fetchData} />} />
            <Route path="documents" element={<Documents refreshStats={fetchData} />} />
            <Route path="conversations" element={<Conversations />} />
            <Route path="chatbot" element={<ChatbotConfig />} />
            <Route path="usage" element={<AIUsage />} />
            <Route path="settings" element={<SettingsPage user={user} />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

// Overview Component
const Overview = ({ stats, refreshStats }) => {
  useEffect(() => {
    refreshStats();
    
    // Auto-refresh stats every 30 seconds
    const interval = setInterval(() => {
      refreshStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  if (!stats || !stats.stats) return <div>Loading stats...</div>;

  const statCards = [
    { label: 'Total Documents', value: stats.stats.total_documents || 0, color: 'blue' },
    { label: 'Processed Documents', value: stats.stats.processed_documents || 0, color: 'green' },
    { label: 'Total Conversations', value: stats.stats.total_conversations || 0, color: 'purple' },
    { label: 'AI Requests', value: stats.stats.total_ai_requests || 0, color: 'orange' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Dashboard Overview</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="text-sm font-medium text-slate-600 mb-2">{card.label}</div>
            <div className="text-3xl font-bold text-slate-900">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Token Usage</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Monthly Tokens</span>
                <span className="font-medium text-slate-900">{(stats.stats.monthly_tokens || 0).toLocaleString()} / {(stats.business?.max_tokens_per_month || 10000).toLocaleString()}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ width: `${Math.min(((stats.stats.monthly_tokens || 0) / (stats.business?.max_tokens_per_month || 10000)) * 100, 100)}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div>
                <div className="text-sm text-slate-600">Input Tokens</div>
                <div className="text-xl font-bold text-slate-900">{(stats.stats.input_tokens || 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm text-slate-600">Output Tokens</div>
                <div className="text-xl font-bold text-slate-900">{(stats.stats.output_tokens || 0).toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Recent Conversations</h2>
          <div className="space-y-3">
            {stats.recent_conversations.length === 0 ? (
              <p className="text-slate-500 text-sm">No conversations yet</p>
            ) : (
              stats.recent_conversations.map((conv) => (
                <div key={conv.id} className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-sm font-medium text-slate-900 truncate">{conv.question}</div>
                  <div className="text-xs text-slate-500 mt-1">{new Date(conv.created_at).toLocaleString()}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Documents Component
const Documents = ({ refreshStats }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    // Auto-refresh documents every 5 seconds if there are pending/processing documents
    const refreshInterval = setInterval(() => {
      const hasPendingOrProcessing = documents.some(
        doc => doc.processing_status === 'pending' || doc.processing_status === 'processing'
      );
      if (hasPendingOrProcessing) {
        fetchDocuments();
      }
    }, 5000);

    return () => clearInterval(refreshInterval);
  }, [documents]);

  const fetchDocuments = async () => {
    try {
      const response = await documentAPI.list();
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('uploaded_file', file);
    formData.append('title', file.name);
    formData.append('business', '');

    try {
      const response = await documentAPI.upload(formData);
      console.log('Upload response:', response.data);
      setFile(null);
      // Clear the file input
      e.target.reset();
      fetchDocuments();
      refreshStats();
      // Start polling for document status updates
      if (response.data.document_id) {
        pollDocumentStatus(response.data.document_id);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Upload failed';
      alert(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const pollDocumentStatus = (documentId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await documentAPI.list();
        const updatedDoc = response.data.documents?.find(d => d.id === documentId);
        if (updatedDoc && (updatedDoc.processing_status === 'completed' || updatedDoc.processing_status === 'failed')) {
          clearInterval(pollInterval);
          fetchDocuments();
          refreshStats();
        }
      } catch (error) {
        console.error('Error polling document status:', error);
        clearInterval(pollInterval);
      }
    }, 3000); // Poll every 3 seconds

    // Stop polling after 5 minutes
    setTimeout(() => clearInterval(pollInterval), 300000);
  };

  const handleDelete = async (documentId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      await documentAPI.delete(documentId);
      fetchDocuments();
      refreshStats();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Delete failed');
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Knowledge Base</h1>

      <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
        <form onSubmit={handleUpload} className="flex items-center space-x-4">
          <input
            type="file"
            onChange={(e) => setFile(e.target.files[0])}
            className="flex-1"
            accept=".pdf,.doc,.docx,.txt"
          />
          <button
            type="submit"
            disabled={!file || uploading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center"
          >
            {uploading ? 'Uploading...' : <><Upload className="h-4 w-4 mr-2" /> Upload</>}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Document</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Uploaded</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {documents.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-12 text-center text-slate-500">
                  No documents uploaded yet
                </td>
              </tr>
            ) : (
              documents.map((doc) => (
                <tr key={doc.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-900">{doc.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      doc.processing_status === 'completed' ? 'bg-green-100 text-green-700' :
                      doc.processing_status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                      doc.processing_status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {doc.processing_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {new Date(doc.uploaded_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Conversations Component
const Conversations = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await conversationAPI.list();
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchConversationDetail = async (id) => {
    try {
      const response = await conversationAPI.get(id);
      setSelectedConversation(response.data);
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Conversations</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">All Conversations</h2>
          </div>
          <div className="divide-y divide-slate-200 max-h-96 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-12 text-center text-slate-500">No conversations yet</div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => fetchConversationDetail(conv.id)}
                  className="p-4 hover:bg-slate-50 cursor-pointer transition"
                >
                  <div className="text-sm font-medium text-slate-900 truncate">{conv.question}</div>
                  <div className="text-xs text-slate-500 mt-1">{new Date(conv.created_at).toLocaleString()}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {selectedConversation && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Conversation Details</h2>
            <div className="space-y-4">
              {selectedConversation.messages?.map((msg) => (
                <div key={msg.id} className={`p-4 rounded-lg ${
                  msg.role === 'user' ? 'bg-blue-50 ml-8' : 'bg-slate-50 mr-8'
                }`}>
                  <div className="text-xs font-medium text-slate-500 uppercase mb-1">{msg.role}</div>
                  <div className="text-sm text-slate-900">{msg.content}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Chatbot Config Component
const ChatbotConfig = () => {
  const [chatbots, setChatbots] = useState([]);
  const [editingChatbot, setEditingChatbot] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchChatbots();
  }, []);

  const fetchChatbots = async () => {
    try {
      const response = await chatbotAPI.list();
      setChatbots(response.data || []);
    } catch (error) {
      console.error('Error fetching chatbots:', error);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingChatbot) {
        await chatbotAPI.update(editingChatbot.id, data);
      } else {
        await chatbotAPI.create(data);
      }
      setShowForm(false);
      setEditingChatbot(null);
      fetchChatbots();
    } catch (error) {
      console.error('Error saving chatbot:', error);
      alert('Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this chatbot?')) return;
    try {
      await chatbotAPI.delete(id);
      fetchChatbots();
    } catch (error) {
      console.error('Error deleting chatbot:', error);
      alert('Delete failed');
    }
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const handleEmbed = async () => {
    try {
      const response = await businessAPI.getWidgetSnippet();
      const snippet = response.data?.script_tag;
      if (!snippet) {
        alert('No widget snippet available yet.');
        return;
      }
      await copyTextToClipboard(snippet);
      alert('Embed snippet copied to clipboard. Paste it into your website HTML.');
    } catch (error) {
      console.error('Error copying embed snippet:', error);
      alert('Unable to load the embed snippet right now.');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Chatbot Configuration</h1>
        <button
          onClick={() => { setEditingChatbot(null); setShowForm(true); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" /> New Chatbot
        </button>
      </div>

      {showForm && (
        <ChatbotForm
          chatbot={editingChatbot}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingChatbot(null); }}
        />
      )}

      <div className="grid gap-4">
        {chatbots.map((chatbot) => (
          <div key={chatbot.id} className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">{chatbot.name}</h3>
                <p className="text-sm text-slate-600 mt-1">{chatbot.description || 'No description'}</p>
                <div className="mt-2 text-xs text-slate-500">
                  Model: {chatbot.model} | Temperature: {chatbot.temperature}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => { setEditingChatbot(chatbot); setShowForm(true); }}
                  className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition"
                  title="Edit chatbot"
                  aria-label="Edit chatbot"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={handleEmbed}
                  className="p-2 rounded-lg text-violet-600 hover:bg-violet-50 hover:text-violet-700 transition"
                  title="Embed chatbot"
                  aria-label="Embed chatbot"
                >
                  <Code2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(chatbot.id)}
                  className="p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition"
                  title="Delete chatbot"
                  aria-label="Delete chatbot"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChatbotForm = ({ chatbot, onSave, onCancel }) => {
  const [formData, setFormData] = useState(chatbot || {
    name: '',
    description: '',
    welcome_message: 'Hello! How can I help you today?',
    system_instructions: 'You are a helpful assistant for question answering over business documents.',
    fallback_response: 'I\'m sorry, I couldn\'t find information about that in the documents.',
    model: 'gemini-3.6-flash',
    temperature: 0.0,
    max_response_length: 1000,
    show_sources: true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        {chatbot ? 'Edit Chatbot' : 'New Chatbot'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            rows={2}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Welcome Message</label>
          <textarea
            value={formData.welcome_message}
            onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            rows={2}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">System Instructions</label>
          <textarea
            value={formData.system_instructions}
            onChange={(e) => setFormData({ ...formData, system_instructions: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            rows={4}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Model</label>
            <select
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="gemini-3.6-flash">Gemini 3.6 Flash</option>
              <option value="gemini-3.5-flash">Gemini 3.5 Flash</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Temperature</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="2"
              value={formData.temperature}
              onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="show_sources"
            checked={formData.show_sources}
            onChange={(e) => setFormData({ ...formData, show_sources: e.target.checked })}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="show_sources" className="text-sm text-slate-700">Show sources in responses</label>
        </div>
        <div className="flex space-x-3">
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Save
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-slate-100 text-slate-700 px-6 py-2 rounded-lg hover:bg-slate-200 transition"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// AI Usage Component
const AIUsage = () => {
  const [usage, setUsage] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, [days]);

  const fetchUsage = async () => {
    setLoading(true);
    try {
      const response = await dashboardAPI.getUsage(days);
      console.log('Usage data:', response.data);
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
      setUsage({ usage_by_model: [], total_records: 0 });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading usage data...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">AI Usage Analytics</h1>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
        <h2 className="font-semibold text-slate-900 mb-4">Usage by Model</h2>
        {!usage.usage_by_model || usage.usage_by_model.length === 0 ? (
          <p className="text-slate-500 text-sm">No usage data available for this period</p>
        ) : (
          <div className="space-y-3">
            {usage.usage_by_model.map((item, index) => (
              <div key={`${item.model}-${index}`} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-slate-900 text-lg">{item.model}</div>
                  <div className="text-sm text-slate-500 mt-1">{item.requests} requests</div>
                </div>
                <div className="flex-1 text-right">
                  <div className="font-medium text-slate-900">{(item.total_tokens || 0).toLocaleString()} tokens</div>
                  <div className="text-sm text-slate-500">
                    {(item.input_tokens || 0).toLocaleString()} in / {(item.output_tokens || 0).toLocaleString()} out
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h2 className="font-semibold text-slate-900 mb-4">Summary</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-slate-50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900">{usage.total_records || 0}</div>
            <div className="text-sm text-slate-600">Total Requests</div>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900">
              {usage.usage_by_model ? usage.usage_by_model.reduce((sum, item) => sum + (item.total_tokens || 0), 0).toLocaleString() : 0}
            </div>
            <div className="text-sm text-slate-600">Total Tokens</div>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-lg">
            <div className="text-2xl font-bold text-slate-900">{usage.usage_by_model ? usage.usage_by_model.length : 0}</div>
            <div className="text-sm text-slate-600">Models Used</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Integration Component
// Settings Component
const SettingsPage = ({ user }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      });
      setPasswordSuccess('Password changed successfully');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Settings</h1>

      <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
        <h2 className="font-semibold text-slate-900 mb-4">Account Information</h2>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Name</label>
            <div className="text-slate-900">{user?.first_name} {user?.last_name}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Email</label>
            <div className="text-slate-900">{user?.email}</div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Role</label>
            <div className="text-slate-900 capitalize">{user?.role}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-900">Change Password</h2>
          {!showPasswordForm && (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              Change Password
            </button>
          )}
        </div>

        {passwordSuccess && (
          <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
            {passwordSuccess}
          </div>
        )}

        {showPasswordForm && (
          <form onSubmit={handlePasswordChange} className="space-y-4">
            {passwordError && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {passwordError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
              <input
                type="password"
                value={passwordData.old_password}
                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
              <input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                required
                minLength={8}
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={changingPassword}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
                  setPasswordError('');
                }}
                className="bg-slate-100 text-slate-700 px-6 py-2 rounded-lg hover:bg-slate-200 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
